/**
 * Turns what a planner typed into plain text of a note into links: Danish phone numbers into
 * tel: links, web addresses (with or without http) and e-mail addresses into links. It works on
 * already sanitized HTML and leaves text that is inside a link alone. No button, only a link.
 * <code>linkifyText</code> does the same for text that is not HTML, such as an inbox message.
 */
// Eight digits, alone or in pairs, optionally behind +45. No lookbehind (older iPhones fail to parse
// one, which would take the whole bundle with it): the character before is checked in the loop.
const PHONE = /(?:\+45[\s.]?)?\d{2}[\s.]?\d{2}[\s.]?\d{2}[\s.]?\d{2}(?![\d])/;
const EMAIL = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/;
const URL = /(?:https?:\/\/|www\.)[^\s<]+|\b(?:[a-z0-9-]+\.)+(?:dk|com|org|net|eu|nu|info)(?:\/[^\s<]*)?/i;
const ANY = new RegExp(`(${EMAIL.source})|(${URL.source})|(${PHONE.source})`, "gi");
// Punctuation that ends a sentence rather than an address.
const TRAILING = /[.,;:!?)\]]+$/;
function makeLink(kind, text) {
    const a = document.createElement("a");
    a.textContent = text;
    if (kind === "mail") {
        a.href = `mailto:${text}`;
    }
    else if (kind === "tel") {
        a.href = `tel:${text.replace(/[^\d+]/g, "")}`;
    }
    else {
        a.href = /^https?:\/\//i.test(text) ? text : `https://${text}`;
        // A page of the site itself opens where the reader is, which in the installed app keeps
        // them in the app; anything else opens in a tab of its own.
        if (a.origin !== window.location.origin) {
            a.target = "_blank";
            a.rel = "noopener noreferrer";
        }
    }
    return a;
}
function linkifyTextNode(node) {
    var _a, _b, _c;
    const text = (_a = node.nodeValue) !== null && _a !== void 0 ? _a : "";
    ANY.lastIndex = 0;
    let match;
    let last = 0;
    const out = document.createDocumentFragment();
    while ((match = ANY.exec(text)) !== null) {
        let found = match[0];
        const trailing = (_c = (_b = TRAILING.exec(found)) === null || _b === void 0 ? void 0 : _b[0]) !== null && _c !== void 0 ? _c : "";
        // A phone number or e-mail address does not end in punctuation we would cut off.
        if (match[3] === undefined && trailing)
            found = found.slice(0, found.length - trailing.length);
        if (found.length === 0)
            continue;
        const kind = match[1] !== undefined ? "mail" : match[2] !== undefined ? "url" : "tel";
        // Eight digits in the middle of a longer number are not a phone number.
        if (kind === "tel" && /[\d+]/.test(text.charAt(match.index - 1))) {
            ANY.lastIndex = match.index + 1;
            continue;
        }
        out.append(text.slice(last, match.index));
        out.append(makeLink(kind, found));
        last = match.index + found.length;
        ANY.lastIndex = last;
    }
    if (last === 0)
        return;
    out.append(text.slice(last));
    node.replaceWith(out);
}
export function linkifyHtml(html) {
    const template = document.createElement("template");
    template.innerHTML = html;
    return linkifyTemplate(template);
}
/** Plain text, escaped, with its addresses, e-mail addresses and phone numbers made links. */
export function linkifyText(text) {
    const template = document.createElement("template");
    template.content.append(document.createTextNode(text));
    return linkifyTemplate(template);
}
function linkifyTemplate(template) {
    var _a, _b, _c;
    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
    const texts = [];
    let current;
    while ((current = walker.nextNode()) !== null) {
        if (!((_c = (_b = ((_a = current.parentElement) !== null && _a !== void 0 ? _a : template.content)).closest) === null || _c === void 0 ? void 0 : _c.call(_b, "a"))) {
            texts.push(current);
        }
    }
    texts.forEach(linkifyTextNode);
    return template.innerHTML;
}
